import { CipherService } from "@bitwarden/common/abstractions/cipher.service";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";

// (orgId, collectionId) -> the accounts shared into that team.
export type TeamAccountsLookup = (orgId: string, collectionId: string) => AccountView[];

// Which accounts are shared into which team (collection). The vault model only keeps the org an
// account's cipher belongs to, not its collections, so this reads them off the cipher itself
// (collectionIds is plaintext on the cached Cipher, no decrypt needed). Ciphers can hold several
// items, so membership is per cipher: everything stored with an account in the same cipher is in
// the same teams. Reads the cipher cache once, so a list of teams can share one lookup.
export async function buildTeamAccountsLookup(
  cipherService: CipherService,
  accounts: AccountView[],
): Promise<TeamAccountsLookup> {
  const ciphers = await cipherService.getAll();
  const collectionIdsByCipher = new Map(ciphers.map((c) => [c.id, c.collectionIds ?? []]));
  return (orgId, collectionId) =>
    accounts.filter(
      (a) =>
        a.isOrganizationOwned(orgId) &&
        collectionIdsByCipher.get(a.toStoreModel().vid)?.includes(collectionId),
    );
}

// The accounts shared into one team.
export async function getAccountsInTeam(
  cipherService: CipherService,
  accounts: AccountView[],
  orgId: string,
  collectionId: string,
): Promise<AccountView[]> {
  if (!accounts.some((a) => a.isOrganizationOwned(orgId))) {
    return [];
  }
  const lookup = await buildTeamAccountsLookup(cipherService, accounts);
  return lookup(orgId, collectionId);
}
